// ============================================================
// VideoCanvas.jsx — preview stage: <video> + live text overlays (v1)
// ------------------------------------------------------------
// Plays the source clip and renders each text overlay only while the playhead
// is within its in/out window. The active overlay is draggable to set its
// frame position (x/y percent). Reports time + duration up to the panel.
//
// The active overlay is pinned visible regardless of the playhead so you can
// position it, which means selection MUST be clearable: an overlay left
// selected sits on top of whatever should actually be showing and, since it
// is the only thing in the layer with pointer-events, swallows pointerdown
// for the overlays beneath it. Clicking empty stage space or pressing Escape
// clears it.
// ============================================================

import React, { useEffect, useRef, useCallback } from 'react';
import { fontFamilyFor } from '../../utils/videoFonts';

// Pointer position as a percentage of the stage box. Module-level so the drag
// handlers keep a stable identity across renders.
const pointerPercent = (e, rect) => ({
  x: ((e.clientX - rect.left) / rect.width) * 100,
  y: ((e.clientY - rect.top) / rect.height) * 100,
});

const clampPercent = (v) => Math.max(0, Math.min(100, v));

export default function VideoCanvas({
  clip,
  overlays = [],
  activeOverlayId,
  playhead = 0,
  playing = false,
  trimStart = 0,
  trimEnd = 0,
  filterCss,
  onTime,
  onDuration,
  onSelectOverlay,
  onDeselectOverlay,
  onMoveOverlay,
}) {
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const dragRef  = useRef(null); // { id, dx, dy } — overlay being dragged + grab offset

  // Keep the <video> in sync with play/pause from the panel.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) v.play().catch(() => {});
    else v.pause();
  }, [playing]);

  // Seek when the panel's playhead jumps (e.g. timeline click) and we're paused.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || playing) return;
    if (Math.abs(v.currentTime - playhead) > 0.25) v.currentTime = playhead;
  }, [playhead, playing]);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    // Loop playback within the trim window.
    if (trimEnd && v.currentTime >= trimEnd) {
      v.currentTime = trimStart;
    }
    onTime?.(v.currentTime);
  };

  const handleLoadedMeta = () => {
    const v = videoRef.current;
    if (v) onDuration?.(v.duration);
  };

  // ── Overlay dragging ────────────────────────────────────────────────────────
  // An overlay is positioned by its CENTRE (translate(-50%,-50%) in the panel's
  // styles), so moving it straight to the cursor would snap its centre under
  // the pointer the instant you grabbed an edge. Instead we record the gap
  // between the grab point and the overlay's centre at pointerdown and carry
  // that gap through the drag, so it moves from wherever you took hold of it.
  const onPointerMove = useCallback((e) => {
    const drag = dragRef.current;
    const el = stageRef.current;
    if (!drag || !el) return;
    const p = pointerPercent(e, el.getBoundingClientRect());
    onMoveOverlay?.(drag.id, {
      x: clampPercent(p.x + drag.dx),
      y: clampPercent(p.y + drag.dy),
    });
  }, [onMoveOverlay]);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
  }, [onPointerMove]);

  const startDrag = (id) => (e) => {
    e.stopPropagation();
    onSelectOverlay?.(id);

    // Grab offset, in stage percentage points. Falls back to 0 (centre-snap,
    // the old behaviour) if the stage box or the overlay can't be resolved.
    const el = stageRef.current;
    const o  = overlays.find((ov) => ov.id === id);
    let dx = 0;
    let dy = 0;
    if (el && o) {
      const p = pointerPercent(e, el.getBoundingClientRect());
      if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
        dx = (Number(o.x) || 0) - p.x;
        dy = (Number(o.y) || 0) - p.y;
      }
    }

    dragRef.current = { id, dx, dy };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  };

  // Clicking empty stage space (the video itself, or any gap between overlays)
  // clears the selection. Overlays stop propagation in startDrag, so a click
  // that lands on an overlay never reaches this handler.
  const handleStagePointerDown = () => {
    if (activeOverlayId) onDeselectOverlay?.();
  };

  // Escape also deselects — but not while the caret is in one of the rail's
  // fields, where it would yank the editor away mid-edit.
  useEffect(() => {
    if (!activeOverlayId) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      onDeselectOverlay?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeOverlayId, onDeselectOverlay]);

  // Show an overlay while the playhead is inside its in/out window, OR whenever
  // it's the active (selected) overlay — so the text you just added or are
  // editing is always visible on the preview to see and position, even after
  // scrubbing the playhead outside its timing window. With nothing selected
  // this reduces to the plain timing window, so only the subtitle that belongs
  // at the current playhead is drawn.
  const visible = overlays.filter(
    (o) => o.id === activeOverlayId || (playhead >= o.inTime && playhead <= o.outTime),
  );

  return (
    <div className="ve-canvas-wrap">
      <div
        className="ve-stage"
        ref={stageRef}
        onPointerDown={handleStagePointerDown}
        data-testid="ve-stage"
      >
        <video
          ref={videoRef}
          className="ve-video"
          src={clip?.url}
          poster={clip?.poster}
          style={{ filter: filterCss || 'none' }}
          playsInline
          muted
          crossOrigin="anonymous"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMeta}
        />
        <div className="ve-overlay-layer">
          {visible.map((o) => (
            <div
              key={o.id}
              className={`ve-overlay${o.id === activeOverlayId ? ' active' : ''}`}
              style={{
                left: `${o.x}%`,
                top: `${o.y}%`,
                color: o.color,
                fontFamily: fontFamilyFor(o.font),
                fontSize: `clamp(12px, ${o.size / 12}vw, ${o.size}px)`,
                textAlign: o.align,
              }}
              onPointerDown={startDrag(o.id)}
            >
              {o.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
